/**
 * ISS-031: stalled-row listing for the App-internal `outbox` task queue.
 *
 * The existing OutboxHealthCard handles `sales_outbox` aggregates; this
 * card surfaces the App-internal task queue (today: backfill_rental_id
 * tasks queued when Sales /internal/rentals/active is unreachable).
 *
 * Click a row → opens StalledRowDrawer (owned by PocketSettings.tsx).
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { listStalledRows } from '@/lib/pocketSettingsApi';
import type {
  InternalStalledRow,
  StalledRowState,
} from '@/types/pocketSettings';

interface Props {
  /** Hoisted by PocketSettings — opens the shared drawer for the clicked row. */
  onRowClick: (row: InternalStalledRow) => void;
  /** Bumps when a mutation in the drawer succeeds, so we re-fetch. */
  reloadKey?: number;
}

const PAGE_SIZE = 25;

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function stateBadge(state: StalledRowState) {
  return state === 'dlq' ? (
    <Badge variant="secondary" className="text-[10px]">DLQ</Badge>
  ) : (
    <Badge variant="outline" className="text-[10px]">Pending</Badge>
  );
}

export function OutboxStalledCard({ onRowClick, reloadKey = 0 }: Props) {
  const [rows, setRows] = useState<InternalStalledRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | StalledRowState>('all');

  const refetch = useCallback(() => {
    let cancelled = false;
    listStalledRows({
      source: 'internal',
      state: filter === 'all' ? undefined : filter,
      limit: PAGE_SIZE,
    })
      .then((res) => {
        if (cancelled) return;
        setRows(res.rows as InternalStalledRow[]);
        setTotal(res.total);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn't load internal outbox.");
      });
    return () => {
      cancelled = true;
    };
  }, [filter]);

  useEffect(() => refetch(), [refetch, reloadKey]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base font-semibold">Internal outbox</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            App-internal task queue (e.g. <code>backfill_rental_id</code>). Click a
            row to inspect its payload, retry, or dead-letter.
          </p>
        </div>
        <div className="flex gap-1 shrink-0">
          {(['all', 'pending', 'dlq'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`text-[11px] px-2 py-0.5 rounded-md border transition-colors ${
                filter === f
                  ? 'bg-muted border-border text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {f === 'all' ? 'All' : f === 'dlq' ? 'DLQ' : 'Pending'}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : rows === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No rows in this filter.</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Task</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead className="hidden sm:table-cell">Target</TableHead>
                  <TableHead className="text-right">Attempts</TableHead>
                  <TableHead className="text-right hidden md:table-cell">Next attempt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    onClick={() => onRowClick(row)}
                    className="cursor-pointer hover:bg-muted/30"
                  >
                    <TableCell className="font-mono text-xs">{row.task_type}</TableCell>
                    <TableCell>{stateBadge(row.state)}</TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground hidden sm:table-cell">
                      {row.target_table}/{row.target_id.slice(0, 8)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{row.attempts}</TableCell>
                    <TableCell className="text-right text-[11px] text-muted-foreground tabular-nums hidden md:table-cell whitespace-nowrap">
                      {formatDate(row.next_attempt_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {total > rows.length && (
              <p className="text-[11px] text-muted-foreground mt-3 text-right tabular-nums">
                Showing {rows.length} of {total}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
